import { useId } from 'react';
import type { FacetKey, GoalEnvelope } from '@/lib/api';
import { GOALS_COPY, RANKING_TERMS, type TermKey } from '@/lib/rankingTerms';
import { FACET_KEYS, normalizeGoals, withFacetTarget, withScalar, type ScalarGoalKey } from '@/lib/goals';
import { ScoreTerm } from '@/components/ScoreTerm';

const INPUT_CLASS =
  'w-24 py-1 px-2 border border-outline-variant rounded-md bg-surface text-on-surface text-sm outline-none focus:border-primary disabled:opacity-60';

/** One facet's target box. Unbounded on purpose: a goal is a position, and a value outside the
 *  bracket's region is shown with a note rather than clamped. Empty = "use the bracket median". */
export function FacetTargetInput({ facet, value, placeholder, disabled, onChange }: {
  facet: FacetKey; value: number | undefined; placeholder?: number | null; disabled?: boolean;
  onChange: (raw: string) => void;
}) {
  return (
    <input
      type="number"
      step="any"
      aria-label={GOALS_COPY.facetTargetLabel(RANKING_TERMS[facet].label)}
      className={INPUT_CLASS}
      value={value ?? ''}
      placeholder={placeholder != null ? placeholder.toFixed(1) : undefined}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const SCALARS: { key: ScalarGoalKey; label: string; term: TermKey; step: string }[] = [
  { key: 'budget_per_card_usd', label: GOALS_COPY.fieldBudget, term: 'budget', step: 'any' },
  { key: 'deck_budget_usd', label: GOALS_COPY.fieldDeckBudget, term: 'deck_budget', step: 'any' },
  { key: 'salt_tolerance', label: GOALS_COPY.fieldSalt, term: 'salt', step: 'any' },
  { key: 'operational_threshold', label: GOALS_COPY.fieldThreshold, term: 'engines_online', step: '1' },
];

/** Budget / deck budget / salt / operational threshold inputs. Empty = unset. */
export function ScalarGoalFields({ value, disabled, onChange }: {
  value: GoalEnvelope; disabled?: boolean; onChange: (next: GoalEnvelope) => void;
}) {
  const g = normalizeGoals(value);
  const uid = useId();   // two editors can be mounted at once (card + modal): ids must not collide
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {SCALARS.map((s) => (
        <div key={s.key} className="flex items-center gap-1.5">
          <label htmlFor={`${uid}-${s.key}`} className="text-sm text-on-surface">{s.label}</label>
          <ScoreTerm term={s.term} />
          <input
            id={`${uid}-${s.key}`}
            type="number"
            step={s.step}
            className={INPUT_CLASS}
            value={g[s.key] ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(withScalar(g, s.key, e.target.value))}
          />
        </div>
      ))}
    </div>
  );
}

/** The whole envelope as plain inputs (modal and variant step): four facet targets + the scalars. */
export function GoalsFields({ value, disabled, onChange }: {
  value: GoalEnvelope; disabled?: boolean; onChange: (next: GoalEnvelope) => void;
}) {
  const g = normalizeGoals(value);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {FACET_KEYS.map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <ScoreTerm term={k} />
            <FacetTargetInput facet={k} value={g.facet_targets[k]} disabled={disabled}
                              onChange={(raw) => onChange(withFacetTarget(g, k, raw))} />
          </div>
        ))}
      </div>
      <ScalarGoalFields value={g} disabled={disabled} onChange={onChange} />
    </div>
  );
}
